/**
 * WhatsApp Pending Count — singleton service.
 *
 * Owns a single backend poll (every 2 min, only when the browser tab is
 * visible) and notifies subscribed React components when the count changes.
 *
 * Used by the sidebar badges on "Whatsapp Lead" + "My Chats" so a single
 * fetch drives all displays — mounting two badges = one poll, not two.
 *
 * API:
 *   waPendingCount.subscribe(setter)  → unsubscribe fn. setter is called
 *                                       immediately with the current count
 *                                       and again whenever the poll updates.
 *   waPendingCount.refresh()          → force an immediate fetch
 *                                       (e.g. after agent sends a reply).
 */

import axios from 'axios';

const AI_PATH      = process.env.REACT_APP_AI_PATH;
const POLL_MS      = 2 * 60 * 1000;   // 2 min — confirmed with Shailendra
const TIMEOUT_MS   = 10000;

let current        = 0;
const subscribers  = new Set();
let timer          = null;
let inFlight       = null;

const getAdminId = () => {
  try { return localStorage.getItem('adminId') || null; } catch (e) { return null; }
};

async function fetchNow() {
  if (inFlight) return inFlight;     // dedupe concurrent refreshes
  const viewerId = getAdminId();
  if (!viewerId || !AI_PATH) return;
  inFlight = axios.get(`${AI_PATH}/api/ai/whatsapp/pending-count`, {
    params: { viewerId },
    timeout: TIMEOUT_MS,
  })
    .then((res) => {
      const next = Number(res.data?.count ?? 0);
      if (next !== current) {
        current = next;
        for (const set of subscribers) set(current);
      }
    })
    .catch((err) => {
      console.warn('[WaPendingCount] fetch failed:', err.message);
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

function startPoll() {
  if (timer) return;
  // Skip the poll while the tab is hidden — saves server load per the
  // 2026-05-26 decision (option A).
  timer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    fetchNow();
  }, POLL_MS);
}

function stopPoll() {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
}

// Refresh as soon as the tab becomes visible — covers the "agent comes back
// from lunch" case so they don't see the stale count for up to 2 min.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fetchNow();
  });
}

const waPendingCount = {
  subscribe(setter) {
    subscribers.add(setter);
    setter(current);
    // Lazy start — no polling until at least one badge is mounted.
    if (subscribers.size === 1) {
      startPoll();
      fetchNow();
    }
    return () => {
      subscribers.delete(setter);
      if (subscribers.size === 0) stopPoll();
    };
  },
  refresh: fetchNow,
  get current() { return current; },
};

export default waPendingCount;
